import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";

// Constants for date formatting
const DATE_FORMAT = "Y-m-d";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_TO_INDEX = Object.fromEntries(MONTHS.map((m, i) => [m, i]));

// Connects to data-controller="featured-inventory"
export default class extends Controller {
  static targets = ["input", "calendar", "checkbox", "featureBlock"];
  static values = {
    minDate: String,
    maxDate: String,
    listableType: String,
    featurableDates: Array,
    notFeaturableDates: Array,
    disableNotFeaturableDates: { type: Boolean, default: false },
  };

  connect() {
    // Use input target if present, otherwise treat the controller element as the input.
    this.inputEl = this.hasInputTarget ? this.inputTarget : this.element;
    this.initFlatpickr();
    this.checkInventory();
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy();
    }
  }

  initFlatpickr() {
    const minDate = this.minDateValue || "today";
    const maxDate = this.maxDateValue || null;

    const options = {
      minDate: minDate,
      maxDate: maxDate,
      inline: true,
      appendTo: this.hasCalendarTarget ? this.calendarTarget : undefined,
      altInput: true,
      altFormat: "l, F j, Y",
      dateFormat: DATE_FORMAT,
      defaultDate: (this.inputEl && this.parseDateString(this.inputEl.value)) || null,
      onChange: this.handleDateChange.bind(this),
      onDayCreate: this.styleDayElement.bind(this),
    };

    // Build disable list
    const disable = [];

    // If not-featurable dates should be disabled, include them
    if (
      this.disableNotFeaturableDatesValue &&
      this.notFeaturableDatesValue.length > 0
    ) {
      disable.push(...this.notFeaturableDatesValue);
    }

    // Apply maxDate to the start date only
    if (maxDate) {
      const max = this.parseToDate(maxDate);
      disable.push((date) => date > max);
    }

    if (disable.length > 0) options.disable = disable;

    this.picker = flatpickr(this.inputEl, options);
  }

  handleDateChange(selectedDates, _dateStr) {
    if (!selectedDates || selectedDates.length === 0) return;

    const start = selectedDates[0];

    // Ensure consumers see the updated value and re-check inventory
    if (this.inputEl) {
      this.inputEl.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.checkInventory();
  }

  styleDayElement(dObj, dStr, fp, dayElem) {
    const dateStr = dayElem.dateObj.toISOString().split('T')[0];

    // Add styling for featurable (available) dates
    if (this.featurableDatesValue.includes(dateStr)) {
      dayElem.classList.add("flatpickr-featurable");
    }

    // Add styling for not featurable dates
    if (this.notFeaturableDatesValue.includes(dateStr)) {
      dayElem.classList.add("flatpickr-not-featurable");
    }
  }

  parseDateString(dateStr) {
    // Parse date string like "Mon Jan 01 2024" back to a Date object
    if (!dateStr || typeof dateStr !== "string") {
      return null;
    }

    const parts = dateStr.split(" ");
    if (parts.length !== 4) {
      return null;
    }

    const month = MONTH_TO_INDEX[parts[1]];
    if (month === undefined) {
      return null;
    }

    const day = parseInt(parts[2], 10);
    const year = parseInt(parts[3], 10);

    if (isNaN(day) || isNaN(year)) {
      return null;
    }

    return new Date(year, month, day);
  }

  async checkInventory() {
    if (!this.listableTypeValue || this.listableTypeValue.trim() === "") {
      return;
    }

    const raw = this.inputEl && this.inputEl.value;
    const startDate = raw ? raw.split(" to ")[0] : null; // only send the start date
    if (!startDate) return;

    try {
      const response = await fetch(
        `/featured_inventory?date=${encodeURIComponent(
          startDate
        )}&listable_type=${encodeURIComponent(this.listableTypeValue)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        this.updateInventoryUI(result);
      }
    } catch (error) {
      console.error("Error checking featured inventory:", error);
    }
  }

  updateInventoryUI(result) {
    if (this.hasCheckboxTarget) {
      if (result.inventory_available) {
        this.checkboxTarget.disabled = false;
        if (this.hasFeatureBlockTarget) {
          this.featureBlockTarget.classList.remove("hidden");
        }
      } else {
        this.checkboxTarget.disabled = true;
        this.checkboxTarget.checked = false;
        if (this.hasFeatureBlockTarget) {
          this.featureBlockTarget.classList.add("hidden");
        }
      }

      this.checkboxTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // Helpers to manage fixed range
  addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  parseToDate(val) {
    if (val instanceof Date) return val;

    // If it's a string in YYYY-MM-DD format, parse it as local timezone
    if (typeof val === 'string') {
      const parts = val.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // months are 0-indexed
        const day = parseInt(parts[2], 10);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          return new Date(year, month, day);
        }
      }
    }

    // Fallback to default parsing
    return new Date(val);
  }
}
